import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import { toast } from 'react-toastify';
import api from '../utils/api';

export const emptyContactForm = {
  account_id: '',
  first_name: '',
  last_name: '',
  email: '',
  cell_number: '',
  office_direct_number: '',
  role_title: '',
  is_primary: false
};

/**
 * Create / edit contact dialog. Associates contact with a company (Account).
 */
const ContactFormDialog = ({
  open,
  onClose,
  onSaved,
  editing = null,
  accountId = '',
  lockAccount = false,
  accounts: accountsProp = null
}) => {
  const [form, setForm] = useState(emptyContactForm);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (Array.isArray(accountsProp)) {
      setAccounts(accountsProp);
      return;
    }
    api.get('/accounts', { params: { page: 1, limit: 500 } })
      .then((res) => setAccounts(res.data?.data || []))
      .catch(() => setAccounts([]));
  }, [open, accountsProp]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        account_id: editing.account_id || accountId || '',
        first_name: editing.first_name || '',
        last_name: editing.last_name || '',
        email: editing.email || '',
        cell_number: editing.cell_number || '',
        office_direct_number: editing.office_direct_number || '',
        role_title: editing.role_title || '',
        is_primary: Boolean(editing.is_primary)
      });
    } else {
      setForm({ ...emptyContactForm, account_id: accountId || '' });
    }
  }, [open, editing, accountId]);

  const save = async () => {
    if (!form.account_id) {
      toast.error('Select a company / account');
      return;
    }
    if (!String(form.first_name || '').trim() || !String(form.email || '').trim()) {
      toast.error('First name and email are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        account_id: form.account_id,
        first_name: String(form.first_name).trim(),
        last_name: String(form.last_name || '').trim(),
        email: String(form.email).trim().toLowerCase(),
        cell_number: String(form.cell_number || '').trim(),
        office_direct_number: String(form.office_direct_number || '').trim(),
        role_title: String(form.role_title || '').trim(),
        is_primary: Boolean(form.is_primary)
      };
      let contact;
      if (editing?.id) {
        const res = await api.put(`/contacts/${editing.id}`, payload);
        contact = res.data?.data || { ...payload, id: editing.id };
        toast.success('Contact updated');
      } else {
        const res = await api.post('/contacts', payload);
        // createContact returns { contact, customer_user? }
        contact = res.data?.data?.contact || res.data?.data;
        toast.success('Contact created');
      }
      onSaved?.(contact);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
          <FormControl fullWidth required disabled={lockAccount && Boolean(accountId || form.account_id)}>
            <InputLabel>Company (Account)</InputLabel>
            <Select
              label="Company (Account)"
              value={form.account_id}
              onChange={(e) => setForm((prev) => ({ ...prev, account_id: e.target.value }))}
            >
              <MenuItem value="" disabled>Select company</MenuItem>
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.company_name || account.account_name}
                  {account.account_code ? ` (${account.account_code})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="First Name"
            required
            value={form.first_name}
            onChange={(e) => setForm((prev) => ({ ...prev, first_name: e.target.value }))}
          />
          <TextField
            label="Last Name"
            value={form.last_name}
            onChange={(e) => setForm((prev) => ({ ...prev, last_name: e.target.value }))}
          />
          <TextField
            label="Email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
          />
          <TextField
            label="Cell Number"
            value={form.cell_number}
            onChange={(e) => setForm((prev) => ({ ...prev, cell_number: e.target.value }))}
          />
          <TextField
            label="Office Direct Number"
            value={form.office_direct_number}
            onChange={(e) => setForm((prev) => ({ ...prev, office_direct_number: e.target.value }))}
          />
          <TextField
            label="Role Title"
            value={form.role_title}
            onChange={(e) => setForm((prev) => ({ ...prev, role_title: e.target.value }))}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactFormDialog;
